import { Typography } from 'components/UI/Typography';
import { AuditEvent } from '../useGetCollaboratorHistoryData';
import { Dialog } from 'features/entry-form-v2/component/Dialog';
import { DateTime } from 'luxon';
import { useQuery } from '@tanstack/react-query';
import { makeRequest } from 'features/entry-form-v2/data/makeRequest';
import { Tab as HeadlessUITab } from '@headlessui/react';
import clsx from 'clsx';
import { useState } from 'react';
import { StatusTypeAccess } from 'store/data/collaborators/types';
import { LanguageType } from 'types';
import { useLanguage } from 'features/hooks/useLanguage';
import { useIsCodelistEnabledForProject } from 'features/codelist/useIsCodeListEnabled';

type Props = {
	onClose: () => void;
	auditEvent: AuditEvent;
	projectId: string;
};

type PermissionItemProps = {
	filled: boolean;
	label: string;
};

const PermissionItem = ({ filled, label }: PermissionItemProps) => (
	<div className="flex items-center gap-2 text-[14px]">
		<span
			className={`w-[12px] h-[12px] rounded-full inline-block ${
				filled ? 'bg-[#3b82f6]' : 'bg-[#d1d5db]'
			}`}
		/>
		<span>{label}</span>
	</div>
);

type PermissionGroupProps = {
	title: string;
	permissions: { key: keyof AuditEvent; label: string }[];
	values: AuditEvent;
	isGrid?: boolean;
};

const PermissionGroup = ({ title, permissions, values, isGrid = true }: PermissionGroupProps) => (
	<div className="mb-6 last:mb-0">
		<h3 className="text-[16px] mb-3 text-gray-700">{title}</h3>
		<div className={isGrid ? 'grid grid-cols-4 gap-3' : 'grid gap-2'}>
			{permissions.map(({ key, label }) => (
				<PermissionItem key={key} filled={Boolean(values[key])} label={label} />
			))}
		</div>
	</div>
);

const TABS = ['general', 'userInterface', 'statuses'] as const;

export function CollaboratorPermissionHistoryModal({ onClose, auditEvent, projectId }: Props) {
	const language = useLanguage();
	const dictionary = DICTIONARY[language];
	const { changedAt, changedByUserName, statusTypeAccesses, projectroleid } = auditEvent;

	const [selectedTab, setSelectedTab] = useState<(typeof TABS)[number]>('general');
	const [modalHeight, setModalHeight] = useState<number>();

	const hasStatus = statusTypeAccesses && statusTypeAccesses.length > 0;
	const hasProjectRole = projectroleid !== NO_PROJECT_ROLE_ID;

	const isCodelistEnabledForProject = useIsCodelistEnabledForProject();

	const statusQuery = useQuery<StatusResponse>({
		enabled: hasStatus,
		queryKey: ['statuses'],
		queryFn: () =>
			makeRequest<StatusResponse>({
				method: 'getStatuses',
				service: 'stats',
				data: {
					projectId: Number(projectId)
				}
			})
	});

	const projectRoleQuery = useQuery({
		enabled: hasProjectRole,
		queryKey: ['projectRoles'],
		queryFn: () =>
			makeRequest<{ projectRoles: { projectRoleId: number; projectRoleName: string }[] }>({
				method: 'getProjectRoles',
				service: 'projects',
				data: {
					project: {
						projectId: projectId
					}
				}
			})
	});

	const getDescription = (auditEvent: AuditEvent): string => {
		const user = auditEvent.username;
		const actions = {
			CREATE: dictionary.modal.added,
			UPDATE: dictionary.modal.updated,
			DELETE: dictionary.modal.removed,
			ACCEPTED: dictionary.modal.accepted,
			REJECTED: dictionary.modal.rejected,
			INVITED: dictionary.modal.invited
		} as const;

		return actions[auditEvent.changedAction](user);
	};

	const getStatusName = (statusTypeAccess: StatusTypeAccess, statusResponse: StatusResponse) => {
		if (statusTypeAccess.statusTypeVariableName === NO_STATUS_ID) {
			return dictionary.noStatus;
		}

		const status = statusResponse.statuses.find(
			status => status.statusName === statusTypeAccess.statusTypeVariableName
		);

		return status?.statusLabel ?? statusTypeAccess.statusTypeVariableName;
	};

	const getProjectRole = (projectRoleId: number) => {
		const projectRole = projectRoleQuery.data?.projectRoles.find(
			role => role.projectRoleId === projectRoleId
		);

		return projectRole?.projectRoleName ?? projectRoleId;
	};

	const formattedTime = DateTime.fromJSDate(new Date(changedAt)).toLocaleString(
		DateTime.DATETIME_MED_WITH_SECONDS
	);

	const generalPermissions: Array<{
		title: string;
		permissions: { key: keyof AuditEvent; label: string }[];
		isGrid?: boolean;
	}> = [
		{
			title: dictionary.permissions.ownData,
			permissions: [
				{
					key: 'accessEntriesOwnonlyRead',
					label: dictionary.permissions.view
				},
				{
					key: 'accessEntriesOwnonlyWrite',
					label: dictionary.permissions.edit
				},
				{
					key: 'accessAnalysisOwnonly',
					label: dictionary.permissions.analyse
				},
				{
					key: 'accessEntriesOwnonlyDelete',
					label: dictionary.permissions.delete
				}
			]
		},
		{
			title: dictionary.modal.groupPermissions,
			permissions: [
				{
					key: 'accessEntriesOrgRead',
					label: dictionary.permissions.view
				},
				{
					key: 'accessEntriesOrgWrite',
					label: dictionary.permissions.edit
				},
				{
					key: 'accessAnalysisOrg',
					label: dictionary.permissions.analyse
				},
				{
					key: 'accessEntriesOrgDelete',
					label: dictionary.permissions.delete
				}
			]
		},
		{
			title: dictionary.permissions.allData,
			permissions: [
				{
					key: 'accessEntriesAllRead',
					label: dictionary.permissions.view
				},
				{
					key: 'accessEntriesAllWrite',
					label: dictionary.permissions.edit
				},
				{
					key: 'accessAnalysisAll',
					label: dictionary.permissions.analyse
				},
				{
					key: 'accessEntriesAllDelete',
					label: dictionary.permissions.delete
				}
			]
		},
		{
			title: dictionary.permissions.additional,
			permissions: [
				...(isCodelistEnabledForProject
					? ([
							{
								key: 'accessCodelistRead',
								label: dictionary.permissions.accessCodelistRead
							},
							{
								key: 'accessCodelistWrite',
								label: dictionary.permissions.accessCodelistWrite
							}
					  ] as const)
					: []),
				{
					key: 'accessExport',
					label: dictionary.permissions.allowExportData
				},
				{
					key: 'accessPersonalData',
					label: dictionary.permissions.allowAccessPersonalData
				},
				{
					key: 'accessVariablesWrite',
					label: dictionary.permissions.allowVariablesEditing
				},
				{
					key: 'accessProjectWrite',
					label: dictionary.permissions.allowProjectEditing
				}
			],
			isGrid: false
		}
	];

	const userInterfacePermissions: Array<{
		title: string;
		permissions: { key: keyof AuditEvent; label: string }[];
		isGrid?: boolean;
	}> = [
		{
			title: dictionary.modal.userInterfacePermissions,
			permissions: [
				{
					key: 'accessModuleProjectDesign',
					label: dictionary.permissions.accessModuleProjectDesign
				},
				{
					key: 'accessModuleCollaborators',
					label: dictionary.permissions.accessModuleCollaborators
				}
			],
			isGrid: false
		}
	];

	return (
		<Dialog open onClose={onClose} title={dictionary.modal.title}>
			<div className="flex flex-col w-full lg:min-w-[600px] min-w-[400px] p-5">
				<div className="bg-indigo-50 rounded-md p-4 mb-5">
					<Typography.H6>{getDescription(auditEvent)}</Typography.H6>
					<div className="text-[14px] text-gray-600">
						{formattedTime} • {dictionary.modal.changedBy(changedByUserName)}
					</div>
				</div>

				<div className="flex">
					<div className="flex flex-col justify-between">
						<HeadlessUITab.Group vertical selectedIndex={TABS.indexOf(selectedTab)}>
							<HeadlessUITab.List>
								<Tab onClick={() => setSelectedTab('general')}>
									{dictionary.modal.tabs.general}
								</Tab>
								<Tab onClick={() => setSelectedTab('userInterface')}>
									{dictionary.modal.tabs.userInterface}
								</Tab>
								{hasStatus && (
									<Tab onClick={() => setSelectedTab('statuses')}>
										{dictionary.modal.tabs.statuses}
									</Tab>
								)}
							</HeadlessUITab.List>
						</HeadlessUITab.Group>

						<span className="text-[14px]">
							{dictionary.modal.projectRole.label}
							{': '}
							{hasProjectRole
								? getProjectRole(projectroleid)
								: dictionary.modal.projectRole.noRole}
						</span>
					</div>

					<div
						className="bg-gray-50 p-5 rounded-md flex-grow"
						style={{ minHeight: modalHeight }}
						ref={e => {
							if (e && !modalHeight) {
								setModalHeight(e.clientHeight);
							}
						}}
					>
						{selectedTab === 'general' &&
							generalPermissions.map((group, index) => (
								<PermissionGroup
									key={index}
									title={group.title}
									permissions={group.permissions}
									values={auditEvent}
									isGrid={group.isGrid}
								/>
							))}

						{selectedTab === 'userInterface' &&
							userInterfacePermissions.map((group, index) => (
								<PermissionGroup
									key={index}
									title={group.title}
									permissions={group.permissions}
									values={auditEvent}
									isGrid={group.isGrid}
								/>
							))}

						{selectedTab === 'statuses' && (
							<>
								{statusQuery.data &&
									statusTypeAccesses &&
									statusTypeAccesses.map(status => (
										<div
											className="mb-6 last:mb-0"
											key={status.statusTypeVariableName}
										>
											<h3 className="text-[16px] mb-3 text-gray-700">
												{getStatusName(status, statusQuery.data)}
											</h3>
											<div className="grid grid-cols-3 gap-3">
												<PermissionItem
													filled={status.viewData}
													label={dictionary.permissions.statuses.view}
												/>
												<PermissionItem
													filled={status.editData}
													label={dictionary.permissions.statuses.edit}
												/>
												<PermissionItem
													filled={status.setStatus}
													label={
														dictionary.permissions.statuses.setStatus
													}
												/>
											</div>
										</div>
									))}
							</>
						)}
					</div>
				</div>
			</div>
		</Dialog>
	);
}

const DICTIONARY = {
	[LanguageType.English]: {
		modal: {
			title: 'Collaborator Permission History',
			changedBy: (name: string) => `Changed by ${name}`,
			groupPermissions: 'Group Permissions',
			added: (user: string) => `${user} was added`,
			removed: (user: string) => `${user} was removed`,
			updated: (user: string) => `${user} was updated`,
			accepted: (user: string) => `${user} has accepted invitation`,
			rejected: (user: string) => `${user} has rejected invitation`,
			invited: (user: string) => `${user} was invited`,
			tabs: {
				general: 'General',
				statuses: 'Statuses',
				userInterface: 'User Interface'
			},
			userInterfacePermissions: 'User Interface Permissions',
			projectRole: {
				label: 'Project Role',
				noRole: 'No Role'
			}
		},
		permissions: {
			view: 'View',
			edit: 'Edit',
			analyse: 'Analyse',
			delete: 'Delete',
			ownData: 'Own Data',
			groupData: 'Group Data',
			allData: 'All Data',
			additional: 'Additional',
			accessCodelistRead: 'Allow read access to codelist',
			accessCodelistWrite: 'Allow write access to codelist',
			allowExportData: 'Allow Export Data',
			allowVariablesEditing: 'Allow Variables Editing',
			allowProjectEditing: 'Allow Project Editing',
			allowAccessPersonalData: 'Allow Access Personal Data',
			accessModuleProjectDesign: 'Access Module Project Design',
			accessModuleCollaborators: 'Access Module Collaborators',
			statuses: {
				view: 'View',
				edit: 'Edit',
				setStatus: 'Set Status'
			}
		},
		noStatus: 'No Status'
	},
	[LanguageType.Norwegian]: {
		modal: {
			title: 'Samarbeidspartner Tillatelseshistorikk',
			changedBy: (name: string) => `Endret av ${name}`,
			groupPermissions: 'Gruppetillatelser',
			added: (user: string) => `${user} ble lagt til`,
			removed: (user: string) => `${user} ble fjernet`,
			updated: (user: string) => `${user} ble oppdatert`,
			accepted: (user: string) => `${user} har akseptert invitasjonen`,
			rejected: (user: string) => `${user} har avvist invitasjonen`,
			invited: (user: string) => `${user} ble invitert`,
			tabs: {
				general: 'Generelt',
				statuses: 'Statuser',
				userInterface: 'Brukergrensesnitt'
			},
			userInterfacePermissions: 'Brukergrensesnitt Tillatelser',
			projectRole: {
				label: 'Prosjektrolle',
				noRole: 'Ingen rolle'
			}
		},
		permissions: {
			view: 'Vis',
			edit: 'Rediger',
			analyse: 'Analyser',
			delete: 'Slett',
			ownData: 'Egen data',
			groupData: 'Gruppedata',
			allData: 'All data',
			additional: 'Tillegg',
			accessCodelistRead: 'Tillat lesing av kodelister',
			accessCodelistWrite: 'Tillat skriving av kodelister',
			allowExportData: 'Tillat eksport av data',
			allowVariablesEditing: 'Tillat redigering av variabler',
			allowProjectEditing: 'Tillat prosjektredigering',
			allowAccessPersonalData: 'Tillat tilgang til personlige data',
			accessModuleProjectDesign: 'Tilgang til modul prosjektdesign',
			accessModuleCollaborators: 'Tilgang til modul samarbeidspartnere',
			statuses: {
				view: 'Vis',
				edit: 'Rediger',
				setStatus: 'Sett status'
			}
		},
		noStatus: 'Ingen status'
	}
} as const;

const Tab = ({ children, onClick }: { children: React.ReactNode; onClick: () => void }) => (
	<HeadlessUITab
		className={({ selected }) =>
			clsx(
				'w-full text-left text-base p-3 border-l-2',
				selected ? 'border-indigo-500' : 'border-transparent'
			)
		}
		onClick={onClick}
	>
		{children}
	</HeadlessUITab>
);

type StatusResponse = {
	statuses: Status[];
};

type Status = {
	statusLabel: string;
	statusName: string;
};

/** When status are created in the system, a default 'No Status' is created with this id */
const NO_STATUS_ID = 'none_generated_by_ledidi';

/** This id indicates that the user has no project role */
const NO_PROJECT_ROLE_ID = 0;
